/**
 * Fritaire cart: drawer open/close, AJAX add/change, section re-render.
 *
 * Conventions:
 *  - [data-cart-drawer]        drawer root (sections/cart-drawer.liquid)
 *  - [data-cart-drawer-inner]  re-rendered fragment
 *  - [data-cart-open]          any element that opens the drawer
 *  - [data-cart-close]         any element that closes the drawer
 *  - [data-cart-count]         cart item count badges
 *  - form[action*="/cart/add"] intercepted and added via AJAX
 */
(function () {
  const drawer = () => document.querySelector('[data-cart-drawer]');

  function openDrawer() {
    const d = drawer();
    if (!d) return;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-cart-open');
  }

  function closeDrawer() {
    const d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-cart-open');
  }

  function setLoading(state) {
    drawer()?.classList.toggle('is-loading', state);
    document.querySelector('[data-cart-page]')?.classList.toggle('is-loading', state);
  }

  function updateBadges(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
    });
  }

  async function refreshDrawer() {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`);
    const sections = await res.json();
    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const fresh = html.querySelector('[data-cart-drawer-inner]');
    const current = document.querySelector('[data-cart-drawer-inner]');
    if (fresh && current) current.replaceWith(fresh);
  }

  // Cart page (/cart) re-renders its own section. JSON-template sections get a generated id
  // (template--<id>__main), so read it from the wrapper instead of hardcoding "main".
  async function refreshCartPage() {
    const page = document.querySelector('[data-cart-page]');
    if (!page) return;
    const sectionId = page.closest('[id^="shopify-section-"]').id.replace('shopify-section-', '');
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart?section_id=${sectionId}`);
    const html = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = html.querySelector('[data-cart-page-inner]');
    const current = document.querySelector('[data-cart-page-inner]');
    if (fresh && current) current.replaceWith(fresh);
  }

  async function refreshAll() {
    const [cartRes] = await Promise.all([fetch('/cart.js'), refreshDrawer(), refreshCartPage()]);
    const cart = await cartRes.json();
    updateBadges(cart.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }

  async function changeLine(key, quantity) {
    setLoading(true);
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await refreshAll();
    } finally {
      setLoading(false);
    }
  }

  async function addToCart(form) {
    const button = form.querySelector('[type="submit"]');
    button?.setAttribute('disabled', 'disabled');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!res.ok) {
        const err = await res.json();
        document.dispatchEvent(new CustomEvent('cart:error', { detail: err }));
        form.querySelector('[data-cart-error]')?.replaceChildren(err.description || err.message || '');
        return;
      }
      await refreshAll();
      openDrawer();
    } finally {
      button?.removeAttribute('disabled');
    }
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-cart-close]')) {
      closeDrawer();
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      changeLine(removeBtn.closest('[data-cart-line]').dataset.cartLine, 0);
      return;
    }
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-cart-line]');
      const input = line.querySelector('[data-qty-input]');
      const next = Math.max(0, parseInt(input.value, 10) + parseInt(qtyBtn.dataset.qtyChange, 10));
      changeLine(line.dataset.cartLine, next);
    }
  });

  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-qty-input]')) return;
    const line = e.target.closest('[data-cart-line]');
    changeLine(line.dataset.cartLine, Math.max(0, parseInt(e.target.value, 10) || 0));
  });

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    if (form.querySelector('[name="return_to"]')) return;
    e.preventDefault();
    addToCart(form);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer()?.classList.contains('is-open')) closeDrawer();
  });

  document.addEventListener('cart:refresh', refreshAll);
  document.addEventListener('cart:open', openDrawer);
})();
